"use client";
import {
  cloneElement,
  isValidElement,
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
  type KeyboardEvent,
} from "react";
import { Slot } from "@radix-ui/react-slot";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "inverse"
  | "outline"
  | "soft"
  | "ghost"
  | "link"
  | "icon"
  | "danger"
  | "danger-secondary";
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  small?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  asChild?: boolean;
};
import { buttonClassName } from "./styles";
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant,
      size,
      small,
      fullWidth,
      isLoading,
      leftIcon,
      rightIcon,
      asChild = false,
      className,
      disabled,
      type,
      children,
      onClickCapture,
      onKeyDownCapture,
      ...props
    },
    ref,
  ) {
    const unavailable = disabled || isLoading;
    const Element = asChild ? Slot : "button";
    const blockActivation = (event: SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const blockActivationKey = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") blockActivation(event);
    };
    // Slot merges child handlers first and lets child attributes win. Override
    // the child before composition so a disabled link cannot re-enable itself
    // through capture/auxiliary handlers or an explicit tabindex/ARIA value.
    const content =
      asChild &&
      unavailable &&
      isValidElement<Record<string, unknown>>(children)
        ? cloneElement(children, {
            disabled: true,
            "aria-disabled": true,
            "aria-busy": isLoading || undefined,
            tabIndex: -1,
            ...(children.type === "a" ? { href: undefined, role: "link" } : {}),
            onClickCapture: blockActivation,
            onAuxClickCapture: blockActivation,
            onDoubleClickCapture: blockActivation,
            onPointerDownCapture: blockActivation,
            onMouseDownCapture: blockActivation,
            onKeyDownCapture: blockActivationKey,
            onKeyUpCapture: blockActivationKey,
          })
        : children;
    return (
      <Element
        {...props}
        ref={ref}
        className={buttonClassName({
          variant,
          size,
          small,
          fullWidth,
          className,
        })}
        {...(!asChild
          ? { type: type ?? "button", disabled: unavailable }
          : type
            ? { type }
            : {})}
        aria-disabled={unavailable || undefined}
        aria-busy={isLoading || undefined}
        {...(asChild && unavailable ? { tabIndex: -1 } : {})}
        onClickCapture={
          unavailable
            ? (event) => {
                event.preventDefault();
                event.stopPropagation();
              }
            : onClickCapture
        }
        onKeyDownCapture={
          unavailable
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }
            : onKeyDownCapture
        }
      >
        {asChild ? (
          content
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </Element>
    );
  },
);
